import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ChakraProvider, extendTheme } from '@chakra-ui/react';
// import GlobalCaptureButton from './components/GlobalCaptureButton';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { auth } from './firebase';
import { signOut } from 'firebase/auth';
// import { SubscriptionProvider } from './contexts/SubscriptionContext';
import LoginScreen from './components/LoginScreen';
import Dashboard from './components/Dashboard';
import Profile from './components/Profile';
import Reports from './components/Reports';
import DeliveryMonitoring from './components/DeliveryMonitoring';
import AssetManagement from './components/AssetManagement';
import PreDepartureChecklist from './components/PreDepartureChecklist';
import ContainerVanChecklist from './components/ContainerVanChecklist';
import ContainerVanPerformCheck from './components/ContainerVanPerformCheck';
import ContainerVanChecklistMonitoring from './components/ContainerVanChecklistMonitoring';
import ContainerVanChecklistReport from './components/ContainerVanChecklistReport';
import SalesOrder from './components/notifications/SalesOrder';
import NotificationDetailPage from './pages/NotificationDetailPage';
import NotificationsPage from './pages/NotificationsPage';
import LoadingSpinner from './components/subComponents/LoadingSpinner';
import PWADebugTools from './components/PWADebugTools';
import AppPushToast from './components/AppPushToast';
import ScrollToTop from './components/ScrollToTop';
import ErrorBoundary from './components/ErrorBoundary';
import NotificationNavigationHandler from './components/NotificationNavigationHandler';

// Chakra UI theme optimized for older users
const theme = extendTheme({
  config: {
    initialColorMode: 'light',
    useSystemColorMode: false,
  },
  styles: {
    global: {
      'html, body': {
        overflowX: 'hidden',
        width: '100%',
        maxWidth: '100%',
      },
      '#root': {
        overflowX: 'hidden',
        width: '100%',
        maxWidth: '100%',
      },
      // Prevent any container from causing horizontal scroll
      '.chakra-container': {
        maxWidth: '100%',
        overflowX: 'hidden',
      },
    },
  },
  fonts: {
    heading: `'Open Sans', sans-serif`,
    body: `'Open Sans', sans-serif`,
  },
  fontSizes: {
    xs: '14px',
    sm: '16px',
    md: '18px',
    lg: '20px',
    xl: '24px',
    '2xl': '28px',
    '3xl': '32px',
    '4xl': '36px',
  },
  colors: {
    brand: {
      50: '#E3F2FD',
      100: '#BBDEFB',
      200: '#90CAF9',
      300: '#64B5F6',
      400: '#42A5F5',
      500: '#2196F3',
      600: '#1E88E5',
      700: '#1976D2',
      800: '#1565C0',
      900: '#0D47A1',
    },
  },
  components: {
    Button: {
      baseStyle: {
        fontWeight: '600',
        borderRadius: '12px',
      },
      sizes: {
        sm: {
          h: '40px',
          fontSize: 'sm',
          px: '16px',
        },
        md: {
          h: '48px',
          fontSize: 'md',
          px: '20px',
        },
        lg: {
          h: '56px',
          fontSize: 'lg',
          px: '24px',
        },
        xl: {
          h: '64px',
          fontSize: 'xl',
          px: '32px',
        },
      },
      defaultProps: {
        size: 'lg',
        colorScheme: 'brand',
      },
    },
    Input: {
      sizes: {
        md: {
          field: {
            fontSize: 'md',
            px: '12px',
            h: '48px',
          },
        },
        lg: {
          field: {
            fontSize: 'lg',
            px: '16px',
            h: '56px',
          },
        },
      },
      defaultProps: {
        size: 'lg',
      },
    },
    Select: {
      sizes: {
        md: {
          field: {
            fontSize: 'md',
            px: '12px',
            h: '48px',
          },
        },
        lg: {
          field: {
            fontSize: 'lg',
            px: '16px',
            h: '56px',
          },
        },
      },
      defaultProps: {
        size: 'lg',
      },
    },
    Textarea: {
      sizes: {
        md: {
          fontSize: 'md',
          px: '12px',
          py: '8px',
        },
        lg: {
          fontSize: 'lg',
          px: '16px',
          py: '12px',
        },
      },
      defaultProps: {
        size: 'lg',
      },
    },
    FormLabel: {
      baseStyle: {
        fontSize: 'lg',
        fontWeight: '600',
        mb: '8px',
      },
    },
  },
});

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  // Only allow if user is logged in
  return user ? <>{children}</> : <Navigate to="/login" replace />;
};

const AppRoutes: React.FC = () => {
  try {
    const { user, userProfile, loading } = useAuth();

    if (loading) {
      return <LoadingSpinner />;
    }

    // Only allow redirect to dashboard if user is allowed
    const userIsAllowed = !!userProfile && userProfile.role === 'user' && userProfile.active !== false;

    return (
      <Routes>
        <Route
          path="/login"
          element={(user && userIsAllowed)  ? <Navigate to="/dashboard" replace /> : <LoginScreen />}
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/delivery-monitoring"
        element={
          <ProtectedRoute>
            <DeliveryMonitoring />
          </ProtectedRoute>
        }
      />
      <Route
        path="/asset-management"
        element={
          <ProtectedRoute>
            <AssetManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pre-departure-checklist"
        element={
          <ProtectedRoute>
            <PreDepartureChecklist />
          </ProtectedRoute>
        }
      />
      <Route
        path="/container-van-checklist"
        element={
          <ProtectedRoute>
            <ContainerVanChecklist />
          </ProtectedRoute>
        }
      />
      <Route
        path="/container-van-checklist/perform"
        element={
          <ProtectedRoute>
            <ContainerVanPerformCheck />
          </ProtectedRoute>
        }
      />
      <Route
        path="/container-van-checklist/monitoring"
        element={
          <ProtectedRoute>
            <ContainerVanChecklistMonitoring />
          </ProtectedRoute>
        }
      />
      <Route
        path="/container-van-checklist/report"
        element={
          <ProtectedRoute>
            <ContainerVanChecklistReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-order/:id"
        element={
          <ProtectedRoute>
            <SalesOrder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications/:id"
        element={
          <ProtectedRoute>
            <NotificationDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
  } catch (error) {
    console.error('Error in AppRoutes:', error);
    // Fallback to login screen if there's an error with auth context
    return <LoginScreen />;
  }
};

const App: React.FC = () => {
  useEffect(() => {
    if (auth.currentUser) {
      signOut(auth);
    }
  }, []);
  return (
    <ErrorBoundary>
      <ChakraProvider theme={theme}>
        <AuthProvider>
          {/* <SubscriptionProvider> */}
            <Router>
              <ScrollToTop />
              <NotificationNavigationHandler />
              {/* <GlobalCaptureButton /> */}
              <AppPushToast />
              <AppRoutes />
              <PWADebugTools />
            </Router>
          {/* </SubscriptionProvider> */}
        </AuthProvider>
      </ChakraProvider>
    </ErrorBoundary>
  );
};

export default App;
